/* eslint-disable no-underscore-dangle */
/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect, useRef, useMemo } from "react"
import { graphql } from "gatsby"
import styled from "styled-components"
import { Helmet } from "react-helmet"

import useHomeAdTargeting from "components/home/hooks/useAdTargeting"
import useProductTracking from "hooks/useProductTracking"
import { useLocation } from "store/contexts/location"
import { ComponentizedPageProvider } from "store/contexts/componentized-page"

import SEO from "components/seo"
import HtlScript from "components/htl-script"
import ErrorBoundary from "components/error-boundary"
import { AboveNavigationAd as HomeAboveNavigationAd } from "components/home/styles"
import ContentfulNavigation from "components/page-components/navigation"
import ContentfulTextBlurb from "components/page-components/text"
import ContentfulGrid from "components/page-components/grid"
import ContentfulImage from "components/page-components/image"
import ContentfulTileGrid from "components/page-components/tile-grid"
import ContentfulAdUnit from "components/page-components/ad-unit"
import ContentfulHero from "components/page-components/hero"
import ContentfulVideoHero from "components/page-components/video-hero"
import ContentfulList from "components/page-components/list"
import ContentfulVideo from "components/page-components/video"
import ContentfulPageNavigationBar from "components/page-components/page-navigation"
import ContentfulCustomCodedBlock from "components/page-components/custom-coded-block"
import ContentfulWebinar from "components/page-components/webinar"
import ContentfulPurchaseForm from "components/page-components/purchase-form"
import ContentfulSignupForm from "components/page-components/sign-up-form"
import ContentfulAnnouncementBar from "components/page-components/announce-bar"
import ContentfulFeaturedProductLayout from "components/page-components/featured-product-layout"
import ContentfulTestimonial from "components/page-components/testimonial"
import ContentfulCard from "components/page-components/card"
import ContentfulTable from "components/page-components/table"
import ContentfulTextWithMedia from "components/page-components/text-with-media"
import ContentfulCarousel from "components/page-components/carousel"
import ContentfulMultiListSignupForm from "components/page-components/multi-list-signup-form"
import AffirmScript from "components/common/affirm-script"

const filterClasses = require("../../utils/filterClasses")
const isAffirmNeeded = require("../../utils/isAffirmNeeded")
const pageHasProduct = require("../../utils/pageHasProduct")

const Page = styled.div`
  margin: 0 auto;
`

const TEMPLATE_TO_NAV_VARIANT_MAP = {
  "Standard": null,
  "Supplements": "supplements",
  "Classes": "classes",
  "Podcats": "podcasts",
  "Category - Lifestyle": "lifestyle",
  "Category - Health": "health",
  "Category - Mindfulness": "mindfulness",
  "Category - Movement": "movement",
  "Category - Food": "food",
  "Category - Relationships": "relationships",
  "Category - Planet": "planet",
}

const ComponentizedPage = ({ data }) => {
  const {
    contentfulComponentizedPage,
    classes: { nodes: classes },
    classBundles: { nodes: classBundles },
  } = data

  const {
    slug,
    pageTitle,
    pageId,
    canonicalUrl,
    seoNoIndex,
    socialShareImage,
    bodyComponents,
    metaDescription,
    template,
    adTargetingId,
    primaryProduct,
    primaryClass,
    pageThemeOverride,
    signupForm,
    shopifyProductVariant,
    navigation,
  } = contentfulComponentizedPage

  let { announcementBar } = contentfulComponentizedPage

  const { location } = useLocation()
  const isHomePage =
    location.pathname === "/" ||
    location.pathname === "/test-home-page" ||
    location.pathname === "/test/home-page-with-dynamic-content"

  // if page has ads, assign unit names
  const hasAds = isHomePage || (bodyComponents && !!bodyComponents.find((c) => c.networkAd))
  const affirmNeeded = isAffirmNeeded(bodyComponents)
  const hasProduct = pageHasProduct(contentfulComponentizedPage)

  const subnavComponent = bodyComponents
    ? bodyComponents.find((c) => c.internal.type === "ContentfulPageNavigationBar")
    : null

  const classesOnPage = filterClasses(classes, contentfulComponentizedPage)
  if (classesOnPage.length) {
    const allAccess = classes.find(
      (c) => c.classId === Number(process.env.GATSBY_ALL_ACCESS_CLS_ID),
    )
    if (allAccess) classesOnPage.push(allAccess)
  }

  const robots = seoNoIndex ? "noindex" : "index"

  const { trackProductRender } = useProductTracking()

  const ref = useRef(null)
  if (primaryProduct) {
    trackProductRender({ elementRef: ref, pageId, variant: shopifyProductVariant })
  } else if (primaryClass) {
    const cls = classesOnPage.find((c) => c.classId === primaryClass.classId)
    trackProductRender({ elementRef: ref, pageId, cls })
  }

  useEffect(() => {
    if (hasProduct) {
      document.querySelector("#footer_disclaimer").style.display = "block"
    }
  }, [hasProduct])

  // component refs for page sub-navigation bar
  const [refs] = useState(bodyComponents?.map(() => React.createRef()) || [])
  const navRef = useRef(null)
  // set webinar announcement to show announcement bar on demand
  const [webinarAnnouncementPromo, setWebinarAnnouncementPromo] = useState({})

  if (webinarAnnouncementPromo && webinarAnnouncementPromo.id) {
    announcementBar = {
      id: webinarAnnouncementPromo.id,
      placement: "Below Navigation",
      backgroundColor: "Category Color",
      countdownTimerEndDate: null,
      mobileLayout: "Show On Mobile",
      announcements: [
        {
          id: webinarAnnouncementPromo.id,
          backgroundColor: "Category Color",
          hideOnPageLoad: false,
          isWebinarPromotion: true,
          internalId: webinarAnnouncementPromo.internalId,
          text: webinarAnnouncementPromo.text,
          navigationAnchor: webinarAnnouncementPromo.navigationAnchor,
          customHtml: webinarAnnouncementPromo.customHtml,
          button: null,
        },
      ],
      ctaButton: webinarAnnouncementPromo.button,
      secondaryCTAButton: null,
    }
  }

  const pageContextData = useMemo(
    () => ({
      template,
      pageTheme: pageThemeOverride || {},
      product: primaryProduct,
      shopifyProductVariant,
      class: primaryClass,
      pageId,
      classesOnPage,
      classBundles,
      isHomePage,
    }),
    [
      template,
      pageThemeOverride,
      primaryProduct,
      shopifyProductVariant,
      primaryClass,
      pageId,
      classesOnPage,
      classBundles,
      isHomePage,
    ],
  )

  let adIndex = 0

  const navigationProps = {
    component: navigation,
    variant: isHomePage ? "home" : TEMPLATE_TO_NAV_VARIANT_MAP[template],
    mainNavRef: navRef,
    hideNav: Boolean(contentfulComponentizedPage?.hideNavigation),
    hideNavOnMobile: Boolean(contentfulComponentizedPage?.hideNavigationOnMobile),
    customNavElement: announcementBar && announcementBar.placement === "On Navigation" && (
      <ErrorBoundary componentType="On Navigation">
        <ContentfulAnnouncementBar component={announcementBar} nested hideOnMobile />
      </ErrorBoundary>
    ),
    beforeNavElement: isHomePage ? (
      <ErrorBoundary componentType="Above Navigation">
        <HomeAboveNavigationAd
          component={{ networkAd: true, device: "Mobile" }}
          unitName="Mobile_Leaderboard"
          hideTitle
        />
      </ErrorBoundary>
    ) : (
      announcementBar &&
      announcementBar.placement === "Above Navigation" && (
        <ErrorBoundary componentType="Above Navigation">
          <ContentfulAnnouncementBar component={announcementBar} isBefore hideOnMobile />
        </ErrorBoundary>
      )
    ),
    subNavElement: announcementBar && announcementBar.placement === "Below Navigation" && (
      <ErrorBoundary componentType="Below Navigation">
        <ContentfulAnnouncementBar component={announcementBar} hideOnMobile />
      </ErrorBoundary>
    ),
    mobileAnnouncement: announcementBar && (
      <ErrorBoundary componentType="announcementBar">
        <ContentfulAnnouncementBar
          component={announcementBar}
          isMobile
          subNavButton={subnavComponent?.ctaButton}
        />
      </ErrorBoundary>
    ),
    template,
  }

  return (
    <ComponentizedPageProvider value={pageContextData}>
      <HtlScript
        targetingOptions={{
          page: slug,
          pageId: adTargetingId,
          ...(isHomePage && useHomeAdTargeting()),
        }}
      />
      <SEO
        title={pageTitle || "Componentized Page"}
        {...(isHomePage && { titleTemplate: "%s" })}
        description={metaDescription}
        robots={robots}
        canonical={canonicalUrl || `${process.env.GATSBY_LOCAL_SITE}/${slug}`}
        image={socialShareImage && socialShareImage[0] && socialShareImage[0].secure_url}
      />
      {isHomePage && (
        <Helmet>
          <meta name="wot-verification" content="44518740f30ee0112e05" />
        </Helmet>
      )}
      {affirmNeeded && (
        <ErrorBoundary componentType="AffirmScript">
          <AffirmScript />
        </ErrorBoundary>
      )}
      <ContentfulNavigation {...navigationProps} />
      {signupForm && (
        <ErrorBoundary componentType="Signup Form">
          <ContentfulSignupForm key={signupForm.id} component={signupForm} popupForm />
        </ErrorBoundary>
      )}
      <Page>
        {bodyComponents?.map((component, idx) => {
          switch (component.internal.type) {
            case "ContentfulTextBlurb":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulTextBlurb component={component} elRef={refs[idx]} />
                </ErrorBoundary>
              )

            case "ContentfulGridLayout":
              if (component.layout === "Tile Hero") {
                return (
                  <ErrorBoundary
                    key={`${component.id}-${idx}`}
                    componentType={component.internal.type}
                  >
                    <ContentfulTileGrid onTop={idx <= 2} component={component} elRef={refs[idx]} />
                  </ErrorBoundary>
                )
              }
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulGrid onTop={idx <= 2} component={component} elRef={refs[idx]} />
                </ErrorBoundary>
              )

            case "ContentfulHero":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulHero onTop={idx <= 2} component={component} elRef={refs[idx]} />
                </ErrorBoundary>
              )

            case "ContentfulVideoHero":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulVideoHero onTop={idx <= 2} component={component} elRef={refs[idx]} />
                </ErrorBoundary>
              )

            case "ContentfulAdUnit": {
              adIndex += 1
              return (
                <ErrorBoundary
                  key={`${component.id}-${adIndex}`}
                  componentType={component.internal.type}
                >
                  <ContentfulAdUnit
                    component={component}
                    unitName={
                      component.unitName ||
                      (adIndex > 1 ? `Breaker_Leaderboard_${adIndex}` : "Breaker_Leaderboard")
                    }
                  />
                </ErrorBoundary>
              )
            }

            case "ContentfulPageNavigationBar":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulPageNavigationBar
                    hideMainNavOnMobile={Boolean(
                      contentfulComponentizedPage?.hideNavigationOnMobile,
                    )}
                    component={component}
                    componentRefs={refs}
                  />
                </ErrorBoundary>
              )

            case "ContentfulTextWithMedia":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulTextWithMedia
                    onTop={idx <= 2}
                    component={component}
                    elRef={refs[idx]}
                  />
                </ErrorBoundary>
              )

            case "ContentfulImage":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulImage onTop={idx <= 2} component={component} elRef={refs[idx]} />
                </ErrorBoundary>
              )

            case "ContentfulList":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulList component={component} elRef={refs[idx]} />
                </ErrorBoundary>
              )

            case "ContentfulVideo":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulVideo component={component} elRef={refs[idx]} />
                </ErrorBoundary>
              )

            case "ContentfulCarousel":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulCarousel onTop={idx <= 2} component={component} elRef={refs[idx]} />
                </ErrorBoundary>
              )

            case "ContentfulSignupForm":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulSignupForm component={component} elRef={refs[idx]} />
                </ErrorBoundary>
              )

            case "ContentfulWebinar":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulWebinar
                    component={component}
                    idx={idx}
                    refs={refs}
                    setWebinarAnnouncement={setWebinarAnnouncementPromo}
                  />
                </ErrorBoundary>
              )

            case "ContentfulFeaturedProductLayout":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulFeaturedProductLayout
                    onTop={idx <= 2}
                    component={component}
                    elRef={refs[idx]}
                  />
                </ErrorBoundary>
              )

            case "ContentfulTestimonial":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulTestimonial onTop={idx <= 2} component={component} elRef={refs[idx]} />
                </ErrorBoundary>
              )

            case "ContentfulCard":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulCard
                    isGrid={false}
                    onTop={idx <= 2}
                    component={component}
                    shopifyProductVariant={shopifyProductVariant}
                    elRef={refs[idx]}
                  />
                </ErrorBoundary>
              )
            case "ContentfulCustomCodedBlock":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulCustomCodedBlock component={component} />
                </ErrorBoundary>
              )

            case "ContentfulTable":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulTable onTop={idx <= 2} component={component} elRef={refs[idx]} />
                </ErrorBoundary>
              )

            case "ContentfulPurchaseForm":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulPurchaseForm component={component} elRef={refs[idx]} />
                </ErrorBoundary>
              )

            case "ContentfulMultiListSignupForm":
              return (
                <ErrorBoundary
                  key={`${component.id}-${idx}`}
                  componentType={component.internal.type}
                >
                  <ContentfulMultiListSignupForm component={component} elRef={refs[idx]} />
                </ErrorBoundary>
              )

            default:
              return (
                <ErrorBoundary key={`${component.id}-${idx}`} componentType="default">
                  <p data-exp-id={component.id}>{component.__typename}</p>
                </ErrorBoundary>
              ) // eslint-disable-line
          }
        })}
      </Page>
    </ComponentizedPageProvider>
  )
}

export const query = graphql`
  query ($id: String) {
    classBundles: allClassBundle {
      nodes {
        ... on ClassBundle {
          id
          savingsPercentage
          title
          classes {
            id
            price
            title
          }
        }
      }
    }

    classes: allClass {
      nodes {
        ... on Class {
          classId
          title
          price
          image
          unlock
          includeInUnlock
          categories
          totalDuration
        }
      }
    }

    contentfulComponentizedPage(id: { eq: $id }) {
      id
      internal {
        type
      }
      slug
      pageTitle
      pageId
      metaDescription
      canonicalUrl
      seoNoIndex
      hideNavigation
      hideNavigationOnMobile
      socialShareImage {
        secure_url
      }
      slug
      updatedAt
      template
      adTargetingId
      primaryProduct
      primaryClass {
        classId
      }
      navigation {
        ... on ContentfulNavigation {
          ...navigationFields
        }
      }
      announcementBar {
        ... on ContentfulAnnouncementBar {
          ...announcementBarFields
        }
      }
      pageThemeOverride {
        ... on ContentfulTheme {
          ...themeFields
        }
      }
      signupForm {
        ... on ContentfulSignupForm {
          ...signUpFormFields
        }
      }
      bodyComponents {
        ... on Node {
          id
          internal {
            type
          }
          ... on ContentfulCustomCodedBlock {
            ...customCodedBlockFields
          }
          ... on ContentfulTextBlurb {
            ...textFields
          }
          ... on ContentfulWebinar {
            ...webinarFields
          }
          ... on ContentfulAnnouncementBar {
            ...announcementBarFields
          }
          ... on ContentfulGridLayout {
            ...gridFields
          }
          ... on ContentfulHero {
            ...heroFields
          }
          ... on ContentfulVideoHero {
            ...videoHeroFields
          }
          ... on ContentfulVideo {
            ...videoFields
          }
          ... on ContentfulImage {
            ...imageFields
          }
          ... on ContentfulCarousel {
            ...carouselFields
          }
          ... on ContentfulTestimonial {
            ...testimonialFields
          }
          ... on ContentfulList {
            ...listFields
          }
          ... on ContentfulSignupForm {
            ...signUpFormFields
          }
          ... on ContentfulTextWithMedia {
            ...textWithMediaFields
          }
          ... on ContentfulPurchaseForm {
            ...purchaseFormFields
          }
          ... on ContentfulFeaturedProductLayout {
            ...featuredProductLayoutFields
          }
          ... on ContentfulTable {
            ...tableFields
          }
          ... on ContentfulAdUnit {
            ...adUnitFields
          }
          ... on ContentfulPageNavigationBar {
            ...pageNavigationBarFields
          }
          ... on ContentfulMultiListSignupForm {
            ...multiListSignupFormFields
          }
        }
      }
      shopifyProductVariant {
        ... on ShopifyProductVariant {
          ...shopifyProductVariantFields
        }
      }
    }
  }
`

export default ComponentizedPage
